import { createClient as createSupabaseClient, SupabaseClient, isAuthApiError } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from './info';
import { SUPABASE_URL } from './url';
import { preserveWorkspaceLock } from '../../platform/auth/workspaceLock';
import type { Database } from '../../types/generated/database';
import { NativeBridge } from '../../services/NativeBridge';

// Re-export generated types so call sites can import from this module
export type { Database } from '../../types/generated/database';
export type { Json } from '../../types/generated/database';
export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];
export type Enums<T extends keyof Database['public']['Enums']> = Database['public']['Enums'][T];

/**
 * GLOBAL SINGLETON SUPABASE CLIENT
 * Prevents "Multiple GoTrueClient instances" warning.
 * Use this instead of creating new clients everywhere.
 */

export const EDGE_FUNCTION_NAME = 'make-server-a476273b';
export { SUPABASE_URL } from './url';
const supabaseUrl = SUPABASE_URL;

/**
 * Shared base URL for all edge function API calls.
 * Import this instead of re-declaring it in each component.
 */
export const API_BASE = `${SUPABASE_URL}/functions/v1/${EDGE_FUNCTION_NAME}`;
export const TENDER_AGENT_API_BASE = `${SUPABASE_URL}/functions/v1/tender-agent`;
export const CONTRACT_DOCUMENT_AGENT_API_BASE = `${SUPABASE_URL}/functions/v1/contract-document-agent`;

export function isEdgeFunctionUrl(url: string): boolean {
  if (!url) return false;
  // Older sessions and external links can still use the project hostname.
  // Keep their retry and network handling during the custom-domain rollout.
  const projectHost = `https://${projectId}.supabase.co`;
  return [
    API_BASE,
    CONTRACT_DOCUMENT_AGENT_API_BASE,
    `${projectHost}/functions/v1/${EDGE_FUNCTION_NAME}`,
    `${projectHost}/functions/v1/contract-document-agent`,
  ]
    .some((base) => url === base || url.startsWith(`${base}/`));
}

let supabaseClientInstance: SupabaseClient<Database> | null = null;
let instanceCount = 0;

export function getSupabaseClient(): SupabaseClient<Database> {
  if (supabaseClientInstance) {
    return supabaseClientInstance;
  }

  instanceCount++;

  if (instanceCount > 1) {
    console.warn('⚠️  Attempted to create multiple Supabase client instances. Using singleton instead.');
  }

  supabaseClientInstance = createSupabaseClient<Database>(supabaseUrl, publicAnonKey, {
    auth: {
      // SINGLE-OWNER refresh. In the native shells the session is a COPY of
      // the native app's token pair, and a refresh token is a rotation
      // FAMILY: when both the web client and the native client refresh the
      // same family on their own independent timers, whichever side uses the
      // now-rotated token second — outside Supabase's ~10s reuse window —
      // gets the entire family revoked, and BOTH sides are signed out. Two
      // hourly timers almost never land inside 10s of each other, which is
      // why the shell kept "logging out on relaunch" at random.
      //
      // So inside a shell the web NEVER refreshes: the native client is the
      // family's only owner, and when this copy's access token expires the
      // existing rescue path (getAuthHeaderToken -> auth:requestSession ->
      // native refreshes and injects a fresh pair) replaces it. In a plain
      // browser/PWA there is no native side and the client refreshes itself
      // exactly as before. isRunningNatively reads the document-start
      // attribute, so it is settled before this module initialises.
      autoRefreshToken: !NativeBridge.isRunningNatively(),
      persistSession: true,
      detectSessionInUrl: true,
      storageKey: 'contract-group-auth',

      // ── No `lock` option, deliberately ────────────────────────────
      // We used to pass a passthrough `lock` because GoTrue serialised auth
      // operations through the Web Locks API, and React Strict Mode's
      // double-mounted effects made it acquire the same named lock twice in
      // quick succession — the first still held by the in-flight
      // getSession() when the cleanup fired — producing:
      //   "Lock 'lock:contract-group-auth' was not released within 5000ms"
      //
      // auth-js 2.107+ coordinates refreshes without a lock by default: an
      // in-tab single-flight dedupes concurrent refreshes, and cross-tab
      // races are resolved server-side by parent-of-active rotation. So the
      // default already does what our passthrough did, without the
      // navigator.locks call that stalled. Supplying any lock is deprecated
      // (it warns on every client construction) and v3 drops the option, so
      // omitting it is also what keeps that upgrade quiet.
      //
      // Reference: @supabase/auth-js/migrations/lockless-coordination.md
    },
  });

  return supabaseClientInstance;
}

/** Set once per page life when we've decided the session is unrecoverable
 *  and have already initiated sign-out + redirect. Stops the next 50
 *  parallel API calls from each calling signOut and reload-spamming. */
let sessionExpiredHandled = false;

/** Called by AuthContext after a native session rescue lands outside this
 *  module's own retry path (e.g. the sessionInjected listener resolves a
 *  fresh session on its own). Without this, a tab that already tripped the
 *  latch would keep treating the (now-healthy) session as dead. */
export function resetSessionExpiredLatch(): void {
  sessionExpiredHandled = false;
}

/** Coalesce concurrent refresh attempts. Without this, a page that
 *  fires 10 fetches at once on mount triggers 10 parallel
 *  refreshSession() calls, which racing against each other can
 *  invalidate one another's refresh tokens. */
let inFlightRefresh: Promise<AuthRefreshResult> | null = null;

/** Result of a refresh attempt. `dead` distinguishes a definitive rejection
 *  of the refresh token (the session really is over) from a transient
 *  failure (network blip, timeout, 5xx) that says nothing about whether the
 *  stored session is still good. */
interface AuthRefreshResult {
  token: string | null;
  dead: boolean;
}

/** True only for a definitive rejection of the refresh token itself —
 *  expired, revoked, reused, or the session no longer exists server-side.
 *  Network failures and `AuthRetryableFetchError` are NOT AuthApiError
 *  instances so they fall through as transient (dead: false), which is the
 *  whole point: a flaky connection at relaunch must not wipe a good session. */
export function isDeadSessionError(error: unknown): boolean {
  // supabase-js intercepts session_not_found server responses and rethrows
  // them as AuthSessionMissingError (NOT an AuthApiError), so that dead case
  // must be recognised by name or it would loop forever as "transient".
  if (error instanceof Error && error.name === 'AuthSessionMissingError') return true;
  if (!isAuthApiError(error)) return false;
  const deadCodes = new Set([
    'refresh_token_not_found',
    'refresh_token_already_used',
    'session_not_found',
    'session_expired',
  ]);
  if (error.code && deadCodes.has(error.code)) return true;
  // Older GoTrue responses can omit `code`. A 400/401 with no code is still
  // the API rejecting the token outright, not a connectivity problem.
  return error.status === 400 || error.status === 401;
}

async function refreshOnce(): Promise<AuthRefreshResult> {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = (async () => {
    try {
      const client = getSupabaseClient();
      const { data: { session }, error } = await client.auth.refreshSession();
      if (error) {
        return { token: null, dead: isDeadSessionError(error) };
      }
      return { token: session?.access_token ?? null, dead: false };
    } catch (error) {
      return { token: null, dead: isDeadSessionError(error) };
    } finally {
      // Clear after a tick so callers within the same microtask share
      // the same result, but a later refresh attempt can re-fire.
      setTimeout(() => { inFlightRefresh = null; }, 0);
    }
  })();
  return inFlightRefresh;
}

type NativeSessionTokens = { access_token?: string; refresh_token?: string };

function getEscrowedNativeSession(): NativeSessionTokens | null {
  if (typeof window === 'undefined') return null;
  return (
    window as unknown as { __QUANTIS_NATIVE_SESSION__?: NativeSessionTokens }
  ).__QUANTIS_NATIVE_SESSION__ ?? null;
}

/** Wait briefly for the native shell to answer an 'auth:requestSession' rescue
 *  request with a 'quantis:native:sessionInjected' CustomEvent (the same event
 *  AuthContext's iOS/macOS session bridge listens for). Resolves null on
 *  timeout so the caller can fall through to sign-out. */
function waitForNativeSessionInjected(timeoutMs: number): Promise<NativeSessionTokens | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: NativeSessionTokens | null) => {
      if (settled) return;
      settled = true;
      // The timeout can fire after the page (or a test's DOM) is gone.
      if (typeof window !== 'undefined') {
        window.removeEventListener('quantis:native:sessionInjected', onInjected);
      }
      clearTimeout(timeoutId);
      resolve(result);
    };
    const onInjected = (event: Event) => {
      finish((event as CustomEvent<NativeSessionTokens>).detail || {});
    };
    window.addEventListener('quantis:native:sessionInjected', onInjected);
    const timeoutId = window.setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Before treating a refresh-token rejection as "the user is logged out",
 * give the native iOS/macOS shell a chance to hand over its own escrowed
 * Supabase session. The shell holds its session independently of this
 * webview's localStorage copy (see AuthContext's `publishMacAuthSession` /
 * `quantis:native:sessionInjected`), so it can be ahead of the web tab —
 * e.g. right after a native sign-in, or after the shell silently refreshed
 * in the background while this tab was asleep. Returns a fresh access token
 * on success, or null if no rescue was possible (plain browser tab, no
 * escrowed session, or the escrowed token is also dead).
 */
let inFlightRescue: Promise<string | null> | null = null;

async function tryNativeSessionRescue(
  client: SupabaseClient<Database>,
  failedRefreshToken: string | undefined,
  rejectedAccessToken?: string
): Promise<string | null> {
  if (!NativeBridge.isRunningNatively()) return null;
  // Coalesce like refreshOnce: when many parallel requests hit a dead session
  // at once, one bridge round-trip and one setSession serve them all.
  if (inFlightRescue) return inFlightRescue;
  inFlightRescue = doNativeSessionRescue(client, failedRefreshToken, rejectedAccessToken);
  try {
    return await inFlightRescue;
  } finally {
    setTimeout(() => { inFlightRescue = null; }, 0);
  }
}

async function doNativeSessionRescue(
  client: SupabaseClient<Database>,
  failedRefreshToken: string | undefined,
  rejectedAccessToken?: string
): Promise<string | null> {

  const attemptSetSession = async (tokens: NativeSessionTokens | null): Promise<string | null> => {
    if (!tokens?.access_token || !tokens.refresh_token) return null;
    if (tokens.refresh_token === failedRefreshToken) return null;
    if (tokens.access_token === rejectedAccessToken) return null;
    try {
      const { data, error } = await client.auth.setSession({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
      });
      if (error || !data.session?.access_token) return null;
      return data.session.access_token;
    } catch {
      return null;
    }
  };

  const rescuedFromEscrow = await attemptSetSession(getEscrowedNativeSession());
  if (rescuedFromEscrow) return rescuedFromEscrow;

  // No usable escrowed session yet — ask the shell to push one, then wait a
  // short window for it. 'auth:requestSession' is the agreed message name;
  // the native side is being built against it in parallel.
  window.QuantisNativeBridge?.send?.('auth:requestSession');
  const injected = await waitForNativeSessionInjected(2500);
  return attemptSetSession(injected);
}

function sessionExpiredError(): Error {
  const error = new Error('Your session has expired. Please sign in again.');
  error.name = 'AuthSessionExpiredError';
  return error;
}

async function expireSession(client: SupabaseClient<Database>): Promise<void> {
  if (sessionExpiredHandled || typeof window === 'undefined') return;
  sessionExpiredHandled = true;
  await preserveWorkspaceLock();
  try { await client.auth.signOut({ scope: 'local' }); } catch { /* best effort */ }
  // The redirect is deferred, so the window can be torn down before it fires
  // (a test environment finishing its file); a bare access would then throw.
  setTimeout(() => {
    if (typeof window !== 'undefined') window.location.replace('/');
  }, 50);
}

/** Coalesces the reload-time 401 burst by the access token that was rejected.
 *  A valid JWT can still receive a legitimate route-level 401, so validate it
 *  with Auth before attempting any refresh or replay. */
const unauthorizedRecoveries = new Map<string, Promise<string | null>>();

export async function recoverAuthTokenAfterUnauthorized(
  rejectedAccessToken: string | null
): Promise<string | null> {
  if (!rejectedAccessToken || rejectedAccessToken === publicAnonKey) return null;

  const existing = unauthorizedRecoveries.get(rejectedAccessToken);
  if (existing) return existing;

  const recovery = (async (): Promise<string | null> => {
    const client = getSupabaseClient();

    // Auth.getUser validates this exact access token without rotating the
    // refresh-token family. That distinction matters for native too: feature
    // routes can legitimately return 401 for a valid signed-in user.
    if (NativeBridge.isRunningNatively()) {
      try {
        const { data, error } = await client.auth.getUser(rejectedAccessToken);
        if (!error && data.user) return null;
        if (!isDeadSessionError(error)) return null;
      } catch (error) {
        if (!isDeadSessionError(error)) return null;
      }

      // Native owns refresh-token rotation. Only after Auth rejects the JWT
      // should the embedded client ask the shell for its current token pair.
      const rescued = await tryNativeSessionRescue(
        client,
        undefined,
        rejectedAccessToken,
      );
      if (rescued && rescued !== rejectedAccessToken) return rescued;
      await expireSession(client);
      return null;
    }

    let refreshToken: string | undefined;
    try {
      const { data, error } = await client.auth.getSession();
      if (error && !isDeadSessionError(error)) return null;
      refreshToken = data.session?.refresh_token;
      const currentToken = data.session?.access_token;
      if (currentToken && currentToken !== rejectedAccessToken) return currentToken;
    } catch {
      // Validation below distinguishes a rejected token from a network blip.
    }

    try {
      const { data, error } = await client.auth.getUser(rejectedAccessToken);
      if (!error && data.user) return null;
      if (!isDeadSessionError(error)) return null;
    } catch (error) {
      if (!isDeadSessionError(error)) return null;
    }

    const result = await refreshOnce();
    if (result.token) {
      return result.token !== rejectedAccessToken ? result.token : null;
    }
    if (!result.dead) return null;

    const rescued = await tryNativeSessionRescue(
      client,
      refreshToken,
      rejectedAccessToken,
    );
    if (rescued && rescued !== rejectedAccessToken) {
      resetSessionExpiredLatch();
      return rescued;
    }

    await expireSession(client);
    return null;
  })().finally(() => {
    unauthorizedRecoveries.delete(rejectedAccessToken);
  });

  unauthorizedRecoveries.set(rejectedAccessToken, recovery);
  return recovery;
}

export async function getAuthHeaderToken(): Promise<string> {
  const client = getSupabaseClient();

  let session: { access_token?: string; refresh_token?: string } | null = null;
  try {
    const { data } = await client.auth.getSession();
    session = data.session;
  } catch {
    // Corrupted local session state — fall through to refresh, which
    // classifies whatever happens next as transient or dead.
  }
  if (session?.access_token) return session.access_token;

  // Session missing or expired — attempt a silent refresh before giving up.
  // Many users keep tabs open for hours; this catches the common "left it
  // open overnight" case without any UX disruption.
  const result = await refreshOnce();
  if (result.token) return result.token;

  if (!result.dead) {
    // Transient failure (network blip, timeout, 5xx) — the stored session
    // and its refresh token are still perfectly good. Do NOT sign out or
    // redirect: that would wipe a healthy session over a connectivity hiccup
    // at relaunch. Fail only this one request; the next attempt gets a fresh
    // shot at refreshing. The error carries a stable name so UI layers that
    // surface error.message verbatim can map it to friendly copy instead.
    const transient = new Error('Could not reach the server. Please try again.');
    transient.name = 'AuthTransientRefreshError';
    throw transient;
  }

  // Definitive rejection: the refresh token itself is dead. Give the native
  // shell a chance to rescue us with its own escrowed session before we
  // treat the user as logged out.
  const rescuedToken = await tryNativeSessionRescue(client, session?.refresh_token);
  if (rescuedToken) {
    resetSessionExpiredLatch();
    return rescuedToken;
  }

  // No rescue available (or not in a native shell). Previously this fell
  // back to publicAnonKey, which made the next 50 user-scoped requests 401
  // silently — the console screenshot showed exactly that storm. Now: sign
  // the dead session out (clears the bad refresh token in localStorage) and
  // bounce to the root so the auth gate shows login. Guarded so a single tab
  // doesn't reload-loop when many parallel API calls all reach this branch
  // at once.
  await expireSession(client);
  throw sessionExpiredError();
}

export async function getAuthHeaders(
  extraHeaders: Record<string, string> = {}
): Promise<Record<string, string>> {
  const token = await getAuthHeaderToken();
  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

// Export singleton instance
export const supabase = getSupabaseClient();

// Backwards-compat alias
export { getSupabaseClient as createClient };

export default supabase;

// Debug helpers (browser only)
if (typeof window !== 'undefined') {
  (window as any).__supabaseClient = supabase;
  (window as any).__checkSupabaseInstances = () => {
    console.log('📊 Supabase client instances created:', instanceCount);
    console.log('✅ Singleton instance:', supabaseClientInstance);
  };
}
